/**
 * CashOutOptions Component
 * 
 * Dual option interface for recipients to cash out:
 * - Option A: Use External Exchange (available now)
 * - Option B: Instant Withdrawal via Widget (coming soon / enabled when ready)
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Building2, 
  Zap, 
  ArrowRight, 
  ExternalLink, 
  Clock, 
  Bell,
  CheckCircle2,
  Globe,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Wallet,
  ArrowUpRight,
  Gift,
  Send,
  AlertCircle,
  Loader2,
  Copy,
  Check
} from 'lucide-react';
import { OFF_RAMP_WIDGET_ENABLED, getExchangesForCountry, SPENDING_OPTIONS } from '../config/cashOutConfig';
import { ethers } from 'ethers';

export default function CashOutOptions({ 
  claimData, 
  userCountry = 'US',
  estimatedLocalAmount = null,
  localCurrency = 'USD',
  onSelectExchange,
  onSelectWidget,
  onBack,
  mnemonic // Added: the claim code for wallet transfers
}) {
  const [selectedOption, setSelectedOption] = useState(null);
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  
  // New: State for "Send to My Wallet" option
  const [walletAddress, setWalletAddress] = useState('');
  const [walletError, setWalletError] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferSuccess, setTransferSuccess] = useState(false);
  const [txHash, setTxHash] = useState('');
  const [copied, setCopied] = useState(false);
  const [transferredToken, setTransferredToken] = useState('USDC'); // Track which token was transferred
  const [sponsorStatus, setSponsorStatus] = useState(''); // Track gas sponsorship status
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  console.log('CashOutOptions BACKEND_URL:', BACKEND_URL);
  
  const exchanges = getExchangesForCountry(userCountry);
  
  const handleWaitlistSubmit = () => {
    if (waitlistEmail) {
      // Store in localStorage for now (could send to backend later)
      const waitlist = JSON.parse(localStorage.getItem('offramp_waitlist') || '[]');
      waitlist.push({ email: waitlistEmail, country: userCountry, date: new Date().toISOString() });
      localStorage.setItem('offramp_waitlist', JSON.stringify(waitlist));
      setWaitlistSubmitted(true);
    }
  };

  // Validate Ethereum/Polygon address
  const isValidAddress = (address) => {
    try {
      return ethers.utils.isAddress(address);
    } catch {
      return false;
    }
  };

  // Handle transfer to user's wallet
  const handleTransferToWallet = async () => {
    if (!walletAddress) {
      setWalletError('Please enter your wallet address');
      return;
    }
    
    if (!isValidAddress(walletAddress)) {
      setWalletError('Please enter a valid Polygon wallet address');
      return;
    }
    
    if (!mnemonic) {
      setWalletError('Unable to access claim wallet. Please try again.');
      return;
    }
    
    setWalletError('');
    setTransferLoading(true);
    setSponsorStatus('Preparing transfer...');
    
    try {
      // Step 1: Get the claim wallet address from mnemonic
      console.log('Starting transfer process...');
      
      // Normalize mnemonic: trim, lowercase, remove extra spaces
      const normalizedMnemonic = mnemonic?.trim().toLowerCase().replace(/\s+/g, ' ');
      console.log('Mnemonic provided:', normalizedMnemonic ? `${normalizedMnemonic.split(' ').length} words` : 'MISSING');
      
      if (!normalizedMnemonic || normalizedMnemonic.split(' ').length !== 12) {
        throw new Error('Invalid mnemonic phrase');
      }
      
      const tempWallet = ethers.Wallet.fromMnemonic(normalizedMnemonic);
      const claimWalletAddress = tempWallet.address;
      console.log('Claim wallet address:', claimWalletAddress);
      
      // Check if user is trying to send to the same wallet
      if (walletAddress.toLowerCase() === claimWalletAddress.toLowerCase()) {
        setWalletError('You cannot transfer to the same wallet! Please enter YOUR personal wallet address (MetaMask, Trust Wallet, Coinbase, etc.) - not the claim wallet.');
        setTransferLoading(false);
        return;
      }
      
      // Step 2: Connect to Polygon network FIRST
      setSponsorStatus('Connecting to Polygon...');
      const RPC_URLS = [
        'https://polygon-bor-rpc.publicnode.com',
        'https://polygon.drpc.org',
        'https://1rpc.io/matic',
        'https://polygon-rpc.com'
      ];
      
      let provider = null;
      let connectionError = null;
      
      for (const rpcUrl of RPC_URLS) {
        try {
          provider = new ethers.providers.JsonRpcProvider(rpcUrl, {
            name: 'polygon',
            chainId: 137
          });
          await provider.getBlockNumber();
          console.log(`Connected to Polygon via ${rpcUrl}`);
          break;
        } catch (err) {
          console.warn(`RPC ${rpcUrl} failed:`, err.message);
          connectionError = err;
          provider = null;
        }
      }
      
      if (!provider) {
        throw new Error(`Failed to connect to Polygon network: ${connectionError?.message || 'All RPC endpoints failed'}`);
      }
      
      // Step 3: Check current gas balance
      let gasBalance = await provider.getBalance(claimWalletAddress);
      let gasBalancePOL = parseFloat(ethers.utils.formatEther(gasBalance));
      console.log('Current claim wallet gas balance:', gasBalancePOL, 'POL');
      
      // Minimum POL needed for gas (ERC20 transfer needs ~0.002 POL at 30 gwei)
      const MIN_GAS_REQUIRED = 0.003;
      
      // Step 4: Request gas sponsorship if needed
      if (gasBalancePOL < MIN_GAS_REQUIRED) {
        console.log(`Wallet needs gas (has ${gasBalancePOL} POL, needs ${MIN_GAS_REQUIRED})`);
        console.log(`Requesting gas for claim wallet: ${claimWalletAddress}`);
        console.log(`API URL: ${BACKEND_URL}/api/gas/sponsor`);
        setSponsorStatus('Preparing gas for transfer...');
        
        let sponsorResponse;
        let sponsorData;
        try {
          sponsorResponse = await fetch(`${BACKEND_URL}/api/gas/sponsor`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ wallet_address: claimWalletAddress })
          });
          
          console.log('Gas sponsor HTTP status:', sponsorResponse.status);
          sponsorData = await sponsorResponse.json();
          console.log('Gas sponsor response:', JSON.stringify(sponsorData));
        } catch (fetchError) {
          console.error('Gas sponsor fetch error:', fetchError);
          throw new Error(`Failed to contact gas sponsor: ${fetchError.message}`);
        }
        
        if (!sponsorResponse.ok) {
          throw new Error(sponsorData?.detail || sponsorData?.message || `Gas sponsorship failed with status ${sponsorResponse.status}`);
        }
        
        if (sponsorData.success && sponsorData.tx_hash) {
          setSponsorStatus('Gas sent! Confirming on blockchain...');
          
          // Wait for the gas transaction to be confirmed - poll every 1.5 seconds for up to 15 seconds
          let attempts = 0;
          const maxAttempts = 10;
          
          while (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 1500));
            gasBalance = await provider.getBalance(claimWalletAddress);
            gasBalancePOL = parseFloat(ethers.utils.formatEther(gasBalance));
            console.log(`Gas balance check ${attempts + 1}/${maxAttempts}: ${gasBalancePOL} POL`);
            
            if (gasBalancePOL >= MIN_GAS_REQUIRED) {
              console.log('Gas confirmed!');
              break;
            }
            attempts++;
            setSponsorStatus(`Confirming... (${Math.round((attempts/maxAttempts)*100)}%)`);
          }
          
          // Final check
          gasBalance = await provider.getBalance(claimWalletAddress);
          gasBalancePOL = parseFloat(ethers.utils.formatEther(gasBalance));
          
          if (gasBalancePOL < MIN_GAS_REQUIRED) {
            throw new Error('Gas is being confirmed on the blockchain. Please try again in 30 seconds.');
          }
          
          setSponsorStatus('Gas confirmed! Sending your funds...');
        } else if (sponsorData.message === "Wallet already has sufficient gas") {
          console.log('Wallet already has gas');
          // Re-check balance just to be sure
          gasBalance = await provider.getBalance(claimWalletAddress);
          gasBalancePOL = parseFloat(ethers.utils.formatEther(gasBalance));
        } else {
          throw new Error(sponsorData.message || 'Gas sponsorship failed');
        }
      } else {
        console.log('Wallet already has sufficient gas:', gasBalancePOL, 'POL');
      }
      
      setSponsorStatus('Sending your funds...');
      
      // Reconstruct wallet from normalized mnemonic
      const wallet = ethers.Wallet.fromMnemonic(normalizedMnemonic).connect(provider);
      
      // Token contracts on Polygon
      const TOKENS = {
        USDC: '0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359', // Native USDC on Polygon
        USDT: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', // USDT on Polygon
        'USDC.e': '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174' // Bridged USDC on Polygon
      };
      
      const TOKEN_ABI = [
        'function transfer(address to, uint256 amount) returns (bool)',
        'function balanceOf(address owner) view returns (uint256)',
        'function decimals() view returns (uint8)',
        'function symbol() view returns (string)'
      ];
      
      // Check balances for all supported tokens
      setSponsorStatus('Checking token balances...');
      let tokenToTransfer = null;
      let tokenBalance = null;
      let tokenContract = null;
      let tokenSymbol = '';
      
      for (const [symbol, address] of Object.entries(TOKENS)) {
        const contract = new ethers.Contract(address, TOKEN_ABI, wallet);
        const balance = await contract.balanceOf(wallet.address);
        
        if (!balance.isZero()) {
          tokenToTransfer = address;
          tokenBalance = balance;
          tokenContract = contract;
          tokenSymbol = symbol;
          console.log(`Found ${symbol} balance:`, ethers.utils.formatUnits(balance, 6));
          break; // Use the first token with balance
        }
      }
      
      if (!tokenContract || !tokenBalance) {
        setWalletError('No USDC or USDT balance found in this wallet. The funds may not have arrived yet.');
        setTransferLoading(false);
        return;
      }
      
      // Final gas balance check before transfer
      gasBalance = await provider.getBalance(claimWalletAddress);
      gasBalancePOL = parseFloat(ethers.utils.formatEther(gasBalance));
      console.log('Final gas balance before transfer:', gasBalancePOL, 'POL');
      
      if (gasBalancePOL < MIN_GAS_REQUIRED) {
        setWalletError('Insufficient gas for transfer. Gas sponsorship may still be pending. Please wait 30 seconds and try again.');
        setTransferLoading(false);
        return;
      }
      
      // Get current gas price and add buffer for Polygon
      setSponsorStatus('Sending transfer...');
      const gasPrice = await provider.getGasPrice();
      // Use at least 30 Gwei or current price + 20%, whichever is higher
      const minGasPrice = ethers.utils.parseUnits('30', 'gwei');
      const adjustedGasPrice = gasPrice.mul(120).div(100); // +20%
      const finalGasPrice = adjustedGasPrice.gt(minGasPrice) ? adjustedGasPrice : minGasPrice;
      
      console.log('Gas price:', ethers.utils.formatUnits(finalGasPrice, 'gwei'), 'gwei');
      console.log('Transferring', tokenSymbol, 'from', wallet.address, 'to', walletAddress);
      console.log('Amount:', ethers.utils.formatUnits(tokenBalance, 6), tokenSymbol);
      console.log('Gas balance:', gasBalancePOL, 'POL');
      
      // Transfer all tokens to user's wallet with proper gas settings
      const tx = await tokenContract.transfer(walletAddress, tokenBalance, {
        gasPrice: finalGasPrice,
        gasLimit: 100000 // ERC20 transfers typically need ~65000 gas
      });
      
      console.log('Transaction submitted:', tx.hash);
      
      setSponsorStatus('Waiting for confirmation...');
      // Wait for confirmation
      const receipt = await tx.wait();
      
      setTxHash(receipt.transactionHash);
      setTransferredToken(tokenSymbol); // Store which token was transferred
      setTransferSuccess(true);
      
    } catch (err) {
      console.error('Transfer error:', err);
      console.error('Full error details:', JSON.stringify(err, null, 2));
      
      const errMsg = err.message || '';
      if (errMsg.includes('insufficient funds') || errMsg.includes('not enough')) {
        // Check if it's token balance or gas
        if (errMsg.includes('gas') || errMsg.includes('POL')) {
          setWalletError('Insufficient POL for gas fees. The gas sponsorship may not have arrived yet. Please wait 30 seconds and try again, or use the Exchange option.');
        } else {
          setWalletError('Insufficient token balance. The funds may not have arrived in this wallet yet.');
        }
      } else if (errMsg.includes('nonce')) {
        setWalletError('Transaction pending. Please wait and try again.');
      } else if (errMsg.includes('network') || errMsg.includes('connect')) {
        setWalletError('Network connection issue. Please check your internet and try again.');
      } else if (errMsg.includes('revert') || errMsg.includes('execution reverted')) {
        setWalletError('Transaction was rejected by the network. This may be due to insufficient balance or token allowance.');
      } else {
        setWalletError(`Transfer failed: ${errMsg || 'Unknown error'}. Please try again or use the Exchange option.`);
      }
    } finally {
      setTransferLoading(false);
      setSponsorStatus('');
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // If user selected exchange option, show exchange list
  if (selectedOption === 'exchange') {
    return (
      <div className="space-y-4">
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Globe className="w-5 h-5 text-emerald-600" />
                Recommended Exchanges
              </CardTitle>
              <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">
                {userCountry}
              </span>
            </div>
            <CardDescription>
              Transfer your USDC to one of these exchanges to convert to local currency
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-3">
            {/* Exchange List */}
            {exchanges.map((exchange, index) => (
              <a
                key={index}
                href={exchange.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`block p-4 rounded-xl border-2 transition-all hover:border-emerald-500 hover:bg-emerald-50 ${
                  exchange.popular ? 'border-emerald-300 bg-emerald-50/50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      exchange.popular ? 'bg-emerald-200' : 'bg-gray-100'
                    }`}>
                      <Building2 className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-800">{exchange.name}</p>
                        {exchange.popular && (
                          <span className="text-xs bg-emerald-600 text-white px-2 py-0.5 rounded-full">
                            Popular
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{exchange.description}</p>
                    </div>
                  </div>
                  <ExternalLink className="w-5 h-5 text-gray-400" />
                </div>
              </a>
            ))}
            
            {/* Spending Option */}
            <div className="pt-2 border-t">
              <p className="text-xs text-gray-500 mb-2">Or spend USDC directly:</p>
              {SPENDING_OPTIONS.map((option, index) => (
                <a
                  key={index}
                  href={option.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:border-purple-400 hover:bg-purple-50 transition-all"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                      <Gift className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{option.name}</p>
                      <p className="text-xs text-gray-500">{option.description}</p>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400" />
                </a>
              ))}
            </div>
            
            {/* How-to Guide Toggle */}
            <button
              onClick={() => setShowGuide(!showGuide)}
              className="w-full flex items-center justify-between p-3 bg-blue-50 rounded-lg text-blue-700 hover:bg-blue-100 transition-all"
            >
              <div className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                <span className="font-medium text-sm">How to cash out - Step by step</span>
              </div>
              {showGuide ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {showGuide && (
              <div className="bg-blue-50 rounded-lg p-4 space-y-3 text-sm">
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center text-blue-800 font-bold text-xs shrink-0">1</div>
                  <div>
                    <p className="font-medium text-blue-800">Create an account</p>
                    <p className="text-blue-600">Sign up on your chosen exchange and complete verification (KYC)</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center text-blue-800 font-bold text-xs shrink-0">2</div>
                  <div>
                    <p className="font-medium text-blue-800">Get your deposit address</p>
                    <p className="text-blue-600">Find your USDC deposit address (make sure to select Polygon network)</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center text-blue-800 font-bold text-xs shrink-0">3</div>
                  <div>
                    <p className="font-medium text-blue-800">Send your USDC</p>
                    <p className="text-blue-600">Transfer USDC from your claim wallet to the exchange</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center text-blue-800 font-bold text-xs shrink-0">4</div>
                  <div>
                    <p className="font-medium text-blue-800">Sell for local currency</p>
                    <p className="text-blue-600">Trade your USDC for {localCurrency} on the exchange</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-200 flex items-center justify-center text-blue-800 font-bold text-xs shrink-0">5</div>
                  <div>
                    <p className="font-medium text-blue-800">Withdraw to bank</p>
                    <p className="text-blue-600">Transfer the {localCurrency} to your bank account</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Wallet Address for Reference */}
            {claimData?.wallet_address && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Your claim wallet address:</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-white px-2 py-1 rounded border flex-1 truncate">
                    {claimData.wallet_address}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(claimData.wallet_address).catch(err => {
                          console.warn('Clipboard write failed:', err);
                          // Fallback: select the text
                          const codeEl = document.querySelector('code');
                          if (codeEl) {
                            const range = document.createRange();
                            range.selectNodeContents(codeEl);
                            const selection = window.getSelection();
                            selection.removeAllRanges();
                            selection.addRange(range);
                          }
                        });
                      }
                    }}
                    className="shrink-0"
                  >
                    Copy
                  </Button>
                </div>
              </div>
            )}
            
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setSelectedOption(null)}
            >
              Back to options
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If user selected "Send to My Wallet" option
  if (selectedOption === 'wallet') {
    // Transfer success view
    if (transferSuccess) {
      return (
        <div className="space-y-4">
          <Card className="border-0 shadow-lg">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-2">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <CardTitle className="text-lg text-emerald-700">Transfer Successful!</CardTitle>
              <CardDescription>
                Your {transferredToken} has been sent to your wallet
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <div className="bg-emerald-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Token</span>
                  <span className="font-semibold">{transferredToken}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Network</span>
                  <span className="font-semibold">Polygon</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">To</span>
                  <span className="font-mono text-xs truncate max-w-[180px]">{walletAddress}</span>
                </div>
              </div>
              
              {txHash && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Transaction Hash:</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-white px-2 py-1 rounded border flex-1 truncate">
                      {txHash}
                    </code>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(txHash)}
                      className="shrink-0"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <a 
                    href={`https://polygonscan.com/tx/${txHash}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-emerald-600 hover:underline flex items-center gap-1 mt-2"
                  >
                    View on PolygonScan
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
              
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setSelectedOption(null);
                  setTransferSuccess(false);
                  setTxHash('');
                  setWalletAddress('');
                }}
              >
                Done
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    
    // Wallet transfer form
    return (
      <div className="space-y-4">
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-blue-600" />
              <CardTitle className="text-lg">Send to My Wallet</CardTitle>
            </div>
            <CardDescription>
              Transfer your funds directly to your own crypto wallet
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {/* Info Box */}
            <div className="bg-blue-50 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-800 mb-1">Important:</p>
              <ul className="text-blue-600 space-y-1 text-xs">
                <li>• Supports <strong>USDC, USDT</strong> on Polygon network</li>
                <li>• Make sure to use the correct address (Polygon/EVM compatible)</li>
                <li>• Transaction is irreversible once confirmed</li>
              </ul>
            </div>
            
            {/* Amount Display */}
            <div className="bg-gray-50 rounded-lg p-3 flex justify-between items-center">
              <span className="text-gray-600 text-sm">Funds to transfer:</span>
              <span className="font-bold text-lg text-emerald-600">
                USDC/USDT
              </span>
            </div>
            
            {/* Wallet Address Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Your Polygon Wallet Address
              </label>
              <Input
                type="text"
                placeholder="0x..."
                value={walletAddress}
                onChange={(e) => {
                  setWalletAddress(e.target.value);
                  setWalletError('');
                }}
                className="font-mono text-sm"
                disabled={transferLoading}
              />
              <p className="text-xs text-gray-500">
                Example: MetaMask, Trust Wallet, Coinbase Wallet address
              </p>
            </div>
            
            {/* Error Message */}
            {walletError && (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{walletError}</span>
              </div>
            )}
            
            {/* Transfer Button */}
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700"
              onClick={handleTransferToWallet}
              disabled={transferLoading || !walletAddress}
            >
              {transferLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {sponsorStatus || 'Transferring...'}
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Transfer Funds
                </>
              )}
            </Button>
            
            {/* Gas info */}
            <p className="text-xs text-center text-gray-500">
              Gas fees are sponsored by RouteFlow - no POL needed!
            </p>
            
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setSelectedOption(null)}
              disabled={transferLoading}
            >
              Back to options
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Main dual-option view
  return (
    <div className="space-y-4">
      <div className="text-center mb-2">
        <h3 className="text-lg font-semibold text-gray-800">Choose how to cash out</h3>
        <p className="text-sm text-gray-500">
          {claimData?.currency || 'USD'} {claimData?.amount?.toFixed(2) || '100.00'}
          {estimatedLocalAmount && localCurrency !== 'USD' && (
            <span className="text-emerald-600"> (~{estimatedLocalAmount} {localCurrency})</span>
          )}
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Option A: External Exchange */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg border-2 ${
            selectedOption === 'exchange' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-emerald-300'
          }`}
          onClick={() => setSelectedOption('exchange')}
        >
          <CardContent className="p-5">
            <div className="flex flex-col items-center text-center space-y-3">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                <Building2 className="w-7 h-7 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Use Exchange</h4>
                <p className="text-sm text-gray-500 mt-1">
                  Transfer to an exchange & sell for local currency
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Available Now
                </span>
              </div>
              <div className="text-xs text-gray-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                15-30 min (varies)
              </div>
              <Button 
                className="w-full bg-emerald-600 hover:bg-emerald-700 mt-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedOption('exchange');
                }}
              >
                Select
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Option B: Send to My Wallet (NEW) */}
        <Card 
          className={`cursor-pointer transition-all hover:shadow-lg border-2 ${
            selectedOption === 'wallet' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
          }`}
          onClick={() => setSelectedOption('wallet')}
        >
          <CardContent className="p-5">
            <div className="flex flex-col items-center text-center space-y-3">
              <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center">
                <Wallet className="w-7 h-7 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">My Wallet</h4>
                <p className="text-sm text-gray-500 mt-1">
                  Send USDC to your own crypto wallet
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Available Now
                </span>
              </div>
              <div className="text-xs text-gray-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Instant (1-2 min)
              </div>
              <Button 
                className="w-full bg-blue-600 hover:bg-blue-700 mt-2"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedOption('wallet');
                }}
              >
                Select
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Option C: Instant Widget (Coming Soon or Active) */}
        <Card 
          className={`transition-all border-2 ${
            OFF_RAMP_WIDGET_ENABLED 
              ? 'cursor-pointer hover:shadow-lg hover:border-purple-300 border-gray-200' 
              : 'border-gray-100 bg-gray-50'
          }`}
          onClick={() => OFF_RAMP_WIDGET_ENABLED && onSelectWidget?.()}
        >
          <CardContent className="p-5">
            <div className="flex flex-col items-center text-center space-y-3">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                OFF_RAMP_WIDGET_ENABLED ? 'bg-purple-100' : 'bg-gray-200'
              }`}>
                <Zap className={`w-7 h-7 ${OFF_RAMP_WIDGET_ENABLED ? 'text-purple-600' : 'text-gray-400'}`} />
              </div>
              <div>
                <h4 className={`font-semibold ${OFF_RAMP_WIDGET_ENABLED ? 'text-gray-800' : 'text-gray-500'}`}>
                  Instant Withdrawal
                </h4>
                <p className={`text-sm mt-1 ${OFF_RAMP_WIDGET_ENABLED ? 'text-gray-500' : 'text-gray-400'}`}>
                  Cash out directly to your bank account
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs">
                {OFF_RAMP_WIDGET_ENABLED ? (
                  <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    Fast & Easy
                  </span>
                ) : (
                  <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Coming Soon
                  </span>
                )}
              </div>
              <div className={`text-xs flex items-center gap-1 ${OFF_RAMP_WIDGET_ENABLED ? 'text-gray-400' : 'text-gray-300'}`}>
                <Clock className="w-3 h-3" />
                1-3 business days
              </div>
              
              {OFF_RAMP_WIDGET_ENABLED ? (
                <Button 
                  className="w-full bg-purple-600 hover:bg-purple-700 mt-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectWidget?.();
                  }}
                >
                  Select This
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <div className="w-full space-y-2 mt-2">
                  {!waitlistSubmitted ? (
                    <>
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          placeholder="your@email.com"
                          value={waitlistEmail}
                          onChange={(e) => setWaitlistEmail(e.target.value)}
                          className="text-sm"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleWaitlistSubmit();
                          }}
                          disabled={!waitlistEmail}
                          className="shrink-0"
                        >
                          <Bell className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-gray-400">Get notified when available</p>
                    </>
                  ) : (
                    <div className="flex items-center justify-center gap-2 text-emerald-600 text-sm">
                      <CheckCircle2 className="w-4 h-4" />
                      We'll notify you!
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {onBack && (
        <Button 
          variant="ghost" 
          className="w-full"
          onClick={onBack}
        >
          Back
        </Button>
      )}
    </div>
  );
}
